import { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import RequireAuth from './components/auth/RequireAuth';
import RequireNoAuth from './components/auth/RequireNoAuth';
import RequireOrg from './components/auth/RequireOrg';
import RequireAdmin from './components/auth/RequireAdmin';
import HomePage from './pages/HomePage';
import SignupPage from './pages/SignupPage';
import SigninPage from './pages/SigninPage';
import OnboardingCreateOrgPage from './pages/OnboardingCreateOrgPage';
import SettingsTeamPage from './pages/SettingsTeamPage';
import SettingsProfilePage from './pages/SettingsProfilePage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import EscalationPoliciesPage from './pages/EscalationPoliciesPage';
import EscalationPolicyDetailPage from './pages/EscalationPolicyDetailPage';
import IncidentsPage from './pages/IncidentsPage';
import IncidentDetailPage from './pages/IncidentDetailPage';
import IntegrationsPage from './pages/IntegrationsPage';
import IntegrationDetailPage from './pages/IntegrationDetailPage';
import SchedulesPage from './pages/SchedulesPage';
import TeamsPage from './pages/TeamsPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import WelcomePage from './pages/WelcomePage';
import { Splash, shouldShowSplash } from './components/Splash';
import { useAuth } from './contexts/AuthContext';

export default function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <SplashGate>
          <Routes>
            <Route path="/welcome" element={<WelcomePage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <HomePage />
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/onboarding/create-org"
              element={
                <RequireAuth>
                  <OnboardingCreateOrgPage />
                </RequireAuth>
              }
            />
            <Route
              path="/accept-invite"
              element={
                <RequireAuth>
                  <AcceptInvitePage />
                </RequireAuth>
              }
            />
            <Route
              path="/services"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <ServicesPage />
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/services/:id"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <ServiceDetailPage />
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/incidents"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <IncidentsPage />
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/incidents/:id"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <IncidentDetailPage />
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/integrations"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <IntegrationsPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/integrations/:integrationId"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <IntegrationDetailPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/escalation-policies"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <EscalationPoliciesPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/schedules"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <SchedulesPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/teams"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <TeamsPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/escalation-policies/:id"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <EscalationPolicyDetailPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/settings/team"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <RequireAdmin>
                      <SettingsTeamPage />
                    </RequireAdmin>
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/settings/profile"
              element={
                <RequireAuth>
                  <RequireOrg>
                    <SettingsProfilePage />
                  </RequireOrg>
                </RequireAuth>
              }
            />
            <Route
              path="/signup"
              element={
                <RequireNoAuth>
                  <SignupPage />
                </RequireNoAuth>
              }
            />
            <Route
              path="/signin"
              element={
                <RequireNoAuth>
                  <SigninPage />
                </RequireNoAuth>
              }
            />
          </Routes>
        </SplashGate>
      </OrgProvider>
    </AuthProvider>
  );
}

// Renders the splash overlay on top of the routes for one session.
// Lives inside AuthProvider so it can read auth-loading state and dismiss
// once that resolves (or after MAX_DISPLAY_MS, whichever is sooner).
function SplashGate({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(() => shouldShowSplash());
  const { loading } = useAuth();
  return (
    <>
      {children}
      {show && <Splash authReady={!loading} onDismiss={() => setShow(false)} />}
    </>
  );
}
